import { queryClient } from "configs/react-query";
import {
  mobileModel,
  mobileVendor,
  osName,
  osVersion,
} from "mobile-device-detect";
import { APIStateKeysEnum } from "services/ApiHooks";
import AppConstants from "services/constants/AppConstants";
import { isLoggedIn } from "utils/authentication";
import { mError, mLog } from "utils/logger";
import zipy from "zipyai";
import { getPersonType } from "./ABTestingHelper";
import { MixpanelAnalytics } from "./Analytics";

const getDefaultProps = async () => {
  const authenticated = isLoggedIn();
  const landingSignupAB = getPersonType("HomePageSignupAB");
  const dashboardType = getPersonType("SimilarProfileType");
  const home_page_ai_search_ab = getPersonType(
    AppConstants.HOME_PAGE_AI_SEARCH_AB
  );
  const dashboardActivationAB: any = getPersonType(
    AppConstants?.DASHBOARD_HOME_ACTIVATION_AB
  );
  const zipySessionUrl = zipy.getCurrentSessionURL();
  // @ts-ignore
  const UA = navigator.userAgent || navigator.vendor || window?.opera;

  const user_data: any = await queryClient.getQueryData([
    APIStateKeysEnum.Profile,
  ]);
  const user_stats: any = await queryClient.getQueryData([
    APIStateKeysEnum.Stats,
  ]);

  const returnObject = {
    State: authenticated ? "Logged In" : "Logged Out",
    os_name: osName,
    os_version: osVersion,
    mobile_model: mobileModel || "NA",
    mobile_vendor: mobileVendor || "NA",
    landingSignupAB: landingSignupAB || "NA",
    dashboard_type: dashboardType || "NA",
    zipy_session_url: zipySessionUrl || "NA",
    home_page_ai_search_ab: home_page_ai_search_ab || "NA",
    dashboard_activation_ab: dashboardActivationAB || "NA",
  };
  returnObject["$os"] = osName || "NA";
  returnObject["$screen_height"] = window?.innerHeight || "NA";
  returnObject["$screen_width"] = window?.innerWidth || "NA";
  returnObject["$user_agent"] = UA || "NA";

  // Adding new logged in props
  if (authenticated && user_data?.id) {
    returnObject["currency_code"] = user_data?.currency?.code;
    returnObject["username"] = user_data?.username;
    returnObject["date_joined"] = user_data?.date_joined;
    returnObject["expertise"] = user_data?.expertise;
    returnObject["following"] = user_data?.following;
    returnObject["is_verified"] = user_data?.is_verified;
    returnObject["premium_tier"] = user_data?.premium_tier;
    returnObject["profile_completion"] = user_data?.profile_completion;
    returnObject["profile_shared"] = user_data?.profile_shared;
    returnObject["upcoming_calls"] = user_data?.upcoming_calls;
    returnObject["profile_views"] = user_stats?.profile_views;
    returnObject["avg_ratings"] = user_stats?.avg_ratings;
    returnObject["completed_calls_count"] = user_stats?.completed_calls_count;
    returnObject["service_counts"] = user_stats?.service_counts;
    returnObject["total_earnings"] = user_stats?.total_earnings;
  }

  return Promise.resolve(returnObject) || returnObject;
};

const actions = {
  identify: (id: string, traits = {}): void => {
    try {
      mLog("Analytics Identify: ", id, traits);
      MixpanelAnalytics.identify(id, traits);
    } catch (err) {
      mError("Analytics identify not captured", err);
    }
  },
  alias: (id: string): void => {
    try {
      mLog("Analytics Alias: ", id);
      MixpanelAnalytics.alias(id);
    } catch (err) {
      mError("Analytics alias not captured", err);
    }
  },
  track: async (name: string, props = {}) => {
    try {
      const defaultProps = await getDefaultProps();
      const mergedProps = { ...defaultProps, ...props };
      MixpanelAnalytics.track(name, mergedProps);
      console.log("Merged Props: ", mergedProps);
      mLog("Analytics Event: ", name, mergedProps);
    } catch (err) {
      mError("Analytics track not captured", err);
    }
  },
  page: async (name: string, props = {}) => {
    const ignore_pages = ["Service Page", "Public Profile", "Dashboard Home"];
    if (!ignore_pages.includes(name)) {
      try {
        const defaultProps = await getDefaultProps();
        const mergedProps = { ...defaultProps, ...props };
        MixpanelAnalytics.page(name, mergedProps);
        mLog("Analytics Page Event: ", name, mergedProps);
      } catch (err) {
        mError("Analytics track not captured", err);
      }
    }
  },
  reset: (): void => {
    try {
      MixpanelAnalytics.reset();
      mLog("Analytics Reset Event!");
    } catch (err) {
      mError("Analytics track not captured", err);
    }
  },
  register: (props = {}): void => {
    try {
      mLog("Analytics Register Event: ", props);
      MixpanelAnalytics.register(props);
    } catch (err) {
      mError("Analytics track not captured", err);
    }
  },
  register_once: (props = {}): void => {
    try {
      mLog("Analytics Register Once Event: ", props);
      MixpanelAnalytics.register_once(props);
    } catch (err) {
      mError("Analytics track not captured", err);
    }
  },
  people: (props = {}): void => {
    try {
      mLog("Analytics People Event: ", props);
      MixpanelAnalytics.people(props);
    } catch (err) {
      mError("Analytics people not captured", err);
    }
  },
  people_once: (props = {}): void => {
    try {
      mLog("Analytics People Event: ", props);
      MixpanelAnalytics.people(props);
    } catch (err) {
      mError("Analytics people not captured", err);
    }
  },
};

export const Analytics = actions;
